<template>
	<!--
		Off-screen export tree: a print-view copy of the results panel, rendered
		only while an image export is being captured. Descendants see a constant
		export flag, so every accordion and insight card renders expanded here
		without mutating the live page (no content flash, no scroll jumps).
	-->
	<div
		class="p-6 text-white rounded-lg bg-neutral-900"
		:style="width ? { width: `${width}px` } : {}"
	>
		<div
			:class="
				wheelMode === 'comparison'
					? 'flex flex-col gap-6 mb-6 sm:flex-row sm:items-start sm:justify-between'
					: ''
			"
		>
			<OverviewSegmentedControl
				v-if="wheelMode === 'comparison'"
				:value="activeMode"
			/>

			<OverviewHeader
				:heading="heading"
				:inline="wheelMode === 'comparison'"
				:wheel-mode="wheelMode"
				:class="{ 'sm:flex-1': wheelMode === 'comparison' }"
			/>
		</div>

		<!-- insight-opened is deliberately not re-emitted: export-driven
			expansion must not reach product analytics. -->
		<OverviewComparisonContent
			v-if="activeMode === 'comparison'"
			:job-offer-data="jobOfferData"
			:applicant-data="applicantData"
			:match-data="matchData"
			:verdict="verdict"
			:side="side"
			wheel-mode="comparison"
		/>

		<OverviewProfileContent
			v-else
			:job-offer-data="jobOfferData"
			:applicant-data="applicantData"
			:wheel-mode="activeMode"
			:hide-key-traits="hideKeyTraits"
		/>

		<!-- Attribution strip: part of the exported image only, never of the
			live UI. -->
		<div
			data-test="export-attribution"
			class="pt-4 mt-6 border-t border-neutral-700"
		>
			<p
				v-if="subjectLabel"
				class="m-0 text-sm text-white uppercase font-heading font-extrabold"
			>
				{{ subjectLabel }}
			</p>
			<p class="m-0 text-xs text-neutral-400">
				{{ $t('personality_results.overview.exported_on', { date }) }}
				· apropos
			</p>
		</div>
	</div>
</template>

<script>
import OverviewHeader from './OverviewHeader.vue'
import OverviewSegmentedControl from './OverviewSegmentedControl.vue'
import OverviewComparisonContent from './OverviewComparisonContent.vue'
import OverviewProfileContent from './OverviewProfileContent.vue'

export default {
	name: 'OverviewExportTree',
	components: {
		OverviewHeader,
		OverviewSegmentedControl,
		OverviewComparisonContent,
		OverviewProfileContent
	},
	// Constant by design: this tree exists only during capture, so every
	// collapsible descendant renders expanded. The live page never provides
	// this flag, which keeps all export state off the screen.
	provide: {
		personalityResultsExport: { exporting: true }
	},
	props: {
		jobOfferData: {
			type: Object,
			default: () => ({})
		},
		applicantData: {
			type: Object,
			default: () => ({})
		},
		matchData: {
			type: Object,
			required: false,
			default: null
		},
		verdict: {
			type: Object,
			required: false,
			default: null
		},
		side: {
			type: String,
			default: 'company',
			validator(value) {
				return ['company', 'applicant'].includes(value)
			}
		},
		// Currently visible mode; the export mirrors what the user sees.
		activeMode: {
			type: String,
			required: true
		},
		wheelMode: {
			type: String,
			required: true
		},
		hideKeyTraits: {
			type: Boolean,
			default: false
		},
		heading: {
			type: String,
			default: ''
		},
		// Resolved by the parent with the same masking rules as the page
		// (talent pool shows first name only); never built here.
		subjectLabel: {
			type: String,
			default: ''
		},
		// Local yyyy-MM-dd, pre-formatted by the parent.
		date: {
			type: String,
			required: true
		},
		// Live panel width in px so the off-screen tree wraps identically;
		// 0 lets it lay out naturally.
		width: {
			type: Number,
			default: 0
		}
	}
}
</script>
